/**
 * The staff console's layout (D39): its own top bar — "ACP Staff console", the stage, the console's sections
 * (`lib/console.ts`' `CONSOLE_NAV`), the signed-in staff member, theme, sign-out — over the page. Presentational: the
 * container (`views/console/ConsoleLayout.tsx`) owns the session, the router and the theme store. Deliberately unlike
 * the customer app's bar (`AppShell`), so nobody mistakes one host for the other.
 *
 * Navigation is plain links (`href`, so open-in-new-tab works) whose clicks go to `onNavigate`.
 */
import { LogOutIcon, ShieldCheckIcon, UserIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { isPlainClick } from '../../lib/links.js';
import type { Theme } from '../../lib/theme.js';
import { cn } from '../../lib/utils.js';
import type { NavItem, ShellUser } from '../AppShell.js';
import { ThemeToggle } from '../ThemeToggle.js';
import { Badge } from '../ui/badge.js';
import { Button } from '../ui/button.js';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '../ui/dropdown-menu.js';

export type ConsoleShellProps = {
  /** The stage the console belongs to (the config's backend label). */
  readonly stage: string;
  readonly user: ShellUser;
  readonly nav: readonly NavItem[];
  readonly onNavigate: (href: string) => void;
  readonly theme: Theme;
  readonly onToggleTheme: () => void;
  readonly onSignOut: () => void;
  /** Above the page: the vendor's announcements, rendered by the container. */
  readonly banner?: ReactNode;
  readonly children: ReactNode;
};

export function ConsoleShell({ stage, user, nav, onNavigate, theme, onToggleTheme, onSignOut, banner, children }: ConsoleShellProps) {
  return (
    <div className="flex min-h-screen flex-col bg-background text-foreground" data-testid="console-shell">
      <header className="flex h-14 items-center gap-4 border-b-2 border-primary bg-card px-4" data-testid="console-bar">
        <div className="flex items-center gap-2 font-semibold">
          <ShieldCheckIcon className="size-5 text-primary" aria-hidden="true" />
          <span>Norna</span>
          <span className="text-muted-foreground">Staff console</span>
        </div>
        <Badge variant="secondary" data-testid="console-stage" title="The stage this console administers">
          {stage}
        </Badge>
        <nav aria-label="Console" className="flex flex-wrap items-center gap-1">
          {nav.map((item) => (
            <a
              key={item.href}
              href={item.href}
              aria-current={item.active ? 'page' : undefined}
              onClick={(event) => {
                if (!isPlainClick(event)) return;
                event.preventDefault();
                onNavigate(item.href);
              }}
              className={cn(
                'rounded-md px-3 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground',
                item.active && 'bg-accent text-accent-foreground',
              )}
            >
              {item.label}
            </a>
          ))}
        </nav>
        <div className="ml-auto flex items-center gap-2">
          <ThemeToggle theme={theme} onToggle={onToggleTheme} />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" data-testid="user-menu">
                <UserIcon />
                <span className="max-w-40 truncate">{user.email ?? user.userId}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel className="font-normal">
                <div className="text-sm font-medium">{user.email ?? user.userId}</div>
                <div className="text-xs text-muted-foreground">{user.tenantId ?? 'no tenant'} · staff</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={onSignOut} data-testid="sign-out">
                <LogOutIcon />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>
      {banner ?? null}
      <main className="flex-1 p-6">{children}</main>
    </div>
  );
}
